"use client";

import type { ILink } from "@ui/components/core/Link";
import type { Media } from "@mw/shared/payload-types";
import type { FC } from "react";
import type { Key } from "react-aria-components";
import type { DemoConfig } from "@/app/_blocks/BlockOmniboxSlides";
import type { OmniboxContent } from "./OmniboxContentCard";

import { clsx } from "clsx";
import { useRef, useState } from "react";
import { Tabs, TabPanel } from "react-aria-components";
import { useTranslations } from "next-intl";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@ui/components/core/Button";
import { OmniboxTabList } from "./OmniboxTabList";
import { OmniboxContentCard } from "./OmniboxContentCard";

const SHOW_NAV_ARROWS_THRESHOLD = 5;

export interface OmniboxSlidesItem {
  content: OmniboxContent;
  demoDescription?: string | null;
  demoTitle?: string | null;
  description?: string | null;
  icon?: Media;
  link?: ILink | null;
  textStyle?: "smallHeadings" | "largeHeadings";
  title: string;
}

export interface OmniboxSlidesProps {
  className?: string;
  items: OmniboxSlidesItem[];
  layout?: "center" | "spaced";
  mobileTabsBelow?: boolean;
  subtitle?: null | string;
  title?: string | null;
}

export const OmniboxSlides: FC<OmniboxSlidesProps> = ({
  title,
  items,
  className,
  subtitle,
  layout = "spaced",
  mobileTabsBelow,
}) => {
  const t = useTranslations("ClientKeys.Globals");
  const [selectedKey, setSelectedKey] = useState<Key>(0);
  const centerRequestRef = useRef(0);

  const handleUserSelection = (key: Key) => {
    setSelectedKey(key);
    centerRequestRef.current += 1;
  };

  const handlePrev = () => {
    const currentIdx = typeof selectedKey === "number" ? selectedKey : 0;
    const newIdx = currentIdx === 0 ? items.length - 1 : currentIdx - 1;
    handleUserSelection(newIdx);
  };

  const handleNext = () => {
    const currentIdx = typeof selectedKey === "number" ? selectedKey : 0;
    const newIdx = (currentIdx + 1) % items.length;
    handleUserSelection(newIdx);
  };

  const showNavArrows = !title && items.length > SHOW_NAV_ARROWS_THRESHOLD;

  return (
    <Tabs
      selectedKey={selectedKey}
      onSelectionChange={handleUserSelection}
      orientation='horizontal'
      className='tw-contents'
    >
      {layout === "center" && (title || subtitle) && (
        <div className='tw-mb-52 tw-flex tw-max-w-xl tw-flex-col tw-gap-16'>
          {title && <h2 className='f-heading-03'>{title}</h2>}
          {subtitle && <p className='f-paragraph-02'>{subtitle}</p>}
        </div>
      )}
      <div className={`tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-2 lg:tw-gap-12 ${className || ""}`}>
        {layout === "spaced" && title && (
          <div className={clsx("tw-flex tw-flex-col tw-gap-16 lg:tw-mb-0 lg:tw-self-start tw-mb-4")}>
            {title && <h2 className='f-heading-03'>{title}</h2>}
            {subtitle && <p className='f-paragraph-02'>{subtitle}</p>}
          </div>
        )}
        <div
          className={clsx(
            "tw-flex tw-flex-col lg:tw-max-w-[600px]",
            mobileTabsBelow && "tw-order-2 lg:tw-order-[unset]",
            showNavArrows
              ? "lg:tw-h-full lg:tw-justify-between lg:tw-row-span-2 lg:tw-row-start-1"
              : layout === "center"
                ? "tw-justify-center lg:tw-row-span-2"
                : title
                  ? "lg:tw-self-end lg:tw-mb-8"
                  : "lg:tw-row-start-1 lg:tw-self-start lg:tw-pt-7",
          )}
        >
          <OmniboxTabList
            items={items}
            selectedKey={selectedKey}
            mobileTabsBelow={mobileTabsBelow}
            centerRequestRef={centerRequestRef}
          />

          {/* Navigation arrows - shown when no title and more than 5 items */}
          {showNavArrows && (
            <div className='tw-hidden tw-gap-3 tw-mt-6 lg:tw-flex'>
              <Button
                aria-label={t("previous_slide")}
                icon={<ChevronLeft size={16} />}
                onClick={handlePrev}
                size='pill-md'
                variant='elevated'
              />
              <Button
                aria-label={t("next_slide")}
                icon={<ChevronRight size={16} />}
                onClick={handleNext}
                size='pill-md'
                variant='elevated'
              />
            </div>
          )}
        </div>
        <div
          className={clsx(
            "tw-relative tw-flex tw-items-center tw-justify-center -tw-mx-2 md:-tw-mx-8 lg:tw-mx-0 lg:tw-col-start-2 lg:tw-row-span-2 lg:tw-row-start-1 lg:tw-justify-end",
            mobileTabsBelow && "tw-order-1 lg:tw-order-[unset]",
          )}
        >
          {items.map((item, idx) => (
            <TabPanel
              key={idx}
              id={idx}
              aria-label={item.demoTitle || item.title}
              className='tw-flex tw-w-full tw-justify-center tw-items-center'
            >
              <OmniboxContentCard
                animationKey={idx}
                content={item.content}
                title={item.demoTitle}
                description={item.demoDescription}
                link={item.link}
                textStyle={item.textStyle}
              />
            </TabPanel>
          ))}
        </div>
      </div>
    </Tabs>
  );
};

// Re-export types for convenience
export type { DemoConfig, OmniboxContent };

export default OmniboxSlides;
